import React, {
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  createContext
} from "react";

// Third-party
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

// OpenLayers
import OlMap from "ol/Map";
import OlView from "ol/View";
import Collection from "ol/Collection";
import { Point } from "ol/geom";
import { createEmpty, extend } from "ol/extent";
import { defaults as defaultControls } from "ol/control.js";
import { defaults as defaultInteractions } from "ol/interaction";

import OlLayerGroup from "ol/layer/Group";
import OlLayerVector from "ol/layer/Vector";
import OlVectorSource from "ol/source/Vector";
import Cluster from "ol/source/Cluster.js";
import { transformExtent } from "ol/proj";

// Components
import OpenLayersMap from "features/Maps/OpenLayersMap";
import { OLMapStyles } from "utils/OpenLayers/OLMapStyles";

// Context
import { AppContext } from "AppProvider";
import { useUiContext } from "context/UiContext";

// Utils
import {
  updateProjectPointsWithType,
  loadIntermediatePoints,
  loadProjectArea,
  loadProjectPoints
} from "utils/OpenLayers/PointsAndAreaUtils";
import { reprojectPointAsCoords } from "utils/OpenLayers/TransformUtils";

// Other
import {
  EPSG_4326,
  EPSG_3857,
  EDITOR_MODES,
  VECTOR_LAYERS_COLLECTION,
  RASTER_LAYERS_COLLECTION,
  RESISTANCE_MAPS_COLLECTION,
  CORRIDORS_COLLECTION,
  COMPARISION_MAPS_COLLECTION,
  EDITABLE_BASELAYERS_COLLECTION,
  PATHS_COLLECTION,
  VECTOR_GEOPROCESSES_COLLECTION,
  RASTER_GEOPROCESSES_COLLECTION,
  SITES_COLLECTION
} from "assets/global";

const MapContext = createContext(null);

// Constants
const maxZoomLevel = 19;
const clusterSourcesDistancePx = 50;

/**
 * Provides the OpenLayers map instance and an API to interact with it.
 * @param {*} props
 * @param {*} props.threads
 * @param {*} props.children
 * @returns {React.ReactElement} The rendered MapProvider component.
 */

function Map2dProvider({ children, threads }) {
  const {
    setMap2DReady,
    fetchBackgrounds,
    backgrounds,
    project,
    scenario,
    state: appState
  } = useContext(AppContext);
  const [{ editMode, bottomDrawerOpen, bottomDrawerHeight }] = useUiContext();

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  // Refs for OpenLayers instances
  const olmapRef = useRef();
  const mapTargetElementRef = useRef(); // Ref for the map div
  const projectIdRef = useRef();

  // Refs for Sources
  const areaSourceRef = useRef(new OlVectorSource());
  const startPointSourceRef = useRef(new OlVectorSource());
  const endPointSourceRef = useRef(new OlVectorSource());
  const intermediatePointSourceRef = useRef(new OlVectorSource());
  const SVPointSourceRef = useRef(new OlVectorSource());
  const highlightedPointsSourceRef = useRef(new OlVectorSource());
  const commentsSourceRef = useRef(new OlVectorSource());
  const commentsClusterSourceRef = useRef(
    new Cluster({
      distance: clusterSourcesDistancePx,
      minDistance: 30,
      source: commentsSourceRef.current
    })
  );

  // Refs for Collections
  const vectorOfLayersRef = useRef(new Collection());
  vectorOfLayersRef.current.name = VECTOR_LAYERS_COLLECTION;
  const rasterLayersCollectionRef = useRef(new Collection());
  rasterLayersCollectionRef.current.name = RASTER_LAYERS_COLLECTION;
  const resistanceCollectionRef = useRef(new Collection());
  resistanceCollectionRef.current.name = RESISTANCE_MAPS_COLLECTION;
  const corridorCollectionRef = useRef(new Collection());
  corridorCollectionRef.current.name = CORRIDORS_COLLECTION;
  const comparisonMapCollectionRef = useRef(new Collection());
  comparisonMapCollectionRef.current.name = COMPARISION_MAPS_COLLECTION;
  const editableBaselayerCollectionRef = useRef(new Collection());
  editableBaselayerCollectionRef.current.name = EDITABLE_BASELAYERS_COLLECTION;
  const optimalPathCollectionRef = useRef(new Collection());
  optimalPathCollectionRef.current.name = PATHS_COLLECTION;
  const sitesCollectionRef = useRef(new Collection());
  sitesCollectionRef.current.name = SITES_COLLECTION;
  const geoprocessVectorCollectionRef = useRef(new Collection());
  geoprocessVectorCollectionRef.current.name = VECTOR_GEOPROCESSES_COLLECTION;
  const geoprocessRasterCollectionRef = useRef(new Collection());
  geoprocessRasterCollectionRef.current.name = RASTER_GEOPROCESSES_COLLECTION;

  // Memoize OLMapStyles to prevent re-creation
  const MapStylesObject = useMemo(() => new OLMapStyles(), []);

  // One-time Map Initialization
  if (!olmapRef.current) {
    // Create Layers
    const layers = [
      new OlLayerVector({ name: "BaseLayers" }),
      new OlLayerGroup({
        name: "rasterLayerGroup",
        layers: rasterLayersCollectionRef.current
      }),
      new OlLayerVector({
        name: "projectArea",
        source: areaSourceRef.current,
        style: MapStylesObject.styleArea,
        zIndex: 0
      }),
      new OlLayerGroup({
        name: "resistanceGroup",
        layers: resistanceCollectionRef.current
      }),
      new OlLayerGroup({
        name: "corridorGroup",
        layers: corridorCollectionRef.current
      }),
      new OlLayerGroup({
        name: "comparisonMapGroup",
        layers: comparisonMapCollectionRef.current
      }),
      new OlLayerGroup({
        name: "vectorLayerGroup",
        layers: vectorOfLayersRef.current
      }),
      new OlLayerGroup({
        name: "editableBaselayerGroup",
        layers: editableBaselayerCollectionRef.current
      }),
      new OlLayerGroup({
        name: "optimalPathGroup",
        layers: optimalPathCollectionRef.current
      }),
      new OlLayerGroup({
        name: "sitesGroup",
        layers: sitesCollectionRef.current
      }),
      new OlLayerVector({
        name: "startPointLayer",
        source: startPointSourceRef.current,
        style: MapStylesObject.styleStart
      }),
      new OlLayerVector({
        name: "endPointLayer",
        source: endPointSourceRef.current,
        style: MapStylesObject.styleEnd
      }),
      new OlLayerVector({
        name: "intermediatePointsLayer",
        source: intermediatePointSourceRef.current,
        style: MapStylesObject.styleInt
      }),
      new OlLayerVector({
        name: "highlightedPointsLayer",
        source: highlightedPointsSourceRef.current,
        style: MapStylesObject.featureSelectedStyle
      }),
      new OlLayerVector({
        name: "StreetView",
        source: SVPointSourceRef.current,
        style: MapStylesObject.SVStyle
      }),
      new OlLayerGroup({
        name: "geoprocessRasterGroup",
        layers: geoprocessRasterCollectionRef.current
      }),
      new OlLayerGroup({
        name: "geoprocessVectorGroup",
        layers: geoprocessVectorCollectionRef.current
      }),
      new OlLayerVector({
        name: "commentsLayer",
        source: commentsClusterSourceRef.current,
        style: MapStylesObject.styleComment,
        pf_comment: true
      })
    ];

    // Create Map
    olmapRef.current = new OlMap({
      controls: defaultControls({
        attribution: false,
        rotate: false,
        zoom: false
      }),
      interactions: defaultInteractions({
        altShiftDragRotate: false,
        pinchRotate: false
      }),
      layers: layers,
      view: new OlView({
        center: reprojectPointAsCoords(new Point([-20, 50])),
        zoom: 2,
        projection: EPSG_3857,
        rotation: 0,
        minZoom: 3,
        maxZoom: maxZoomLevel
      })
    });
  }

  // API Functions
  const updateMap = useCallback(() => {
    const isEditMode =
      editMode && editMode !== EDITOR_MODES.SCENARIO_POINTS_EDITOR;

    loadProjectArea(project.area, areaSourceRef.current);
    loadProjectPoints(
      {
        project: {
          start: project.start_point,
          end: project.end_point
        },
        scenario: {
          start: scenario?.start_point,
          end: scenario?.end_point
        }
      },
      {
        project: {
          start: startPointSourceRef.current,
          end: endPointSourceRef.current
        },
        scenario: {
          start: startPointSourceRef.current,
          end: endPointSourceRef.current
        }
      },
      isEditMode
    );

    if (isEditMode) {
      updateProjectPointsWithType(
        { start: project.start_point, end: project.end_point },
        { start: startPointSourceRef.current, end: endPointSourceRef.current },
        "ghost"
      );
    }

    intermediatePointSourceRef.current.clear();
    if (scenario?.intermediate_points) {
      loadIntermediatePoints(
        scenario.intermediate_points,
        intermediatePointSourceRef.current
      );
    }
  }, [
    editMode,
    project?.area,
    project?.end_point,
    project?.start_point,
    scenario?.end_point,
    scenario?.start_point,
    scenario?.intermediate_points
  ]);

  const centerView = useCallback(
    feat_extent => {
      try {
        let newExtent = feat_extent;
        if (!newExtent && appState.project.area_bbox) {
          newExtent = transformExtent(
            appState.project.area_bbox,
            EPSG_4326,
            EPSG_3857
          );
        }
        if (newExtent) {
          olmapRef.current.getView().fit(newExtent, {
            padding: [50, 50, 50, 50],
            duration: 2000,
            rotation: 0
          });
        }
      } catch (e) {
        enqueueSnackbar(t("error.Viewcentererror"), { variant: "error" });
      }
    },
    [appState.project.area_bbox, enqueueSnackbar, t]
  );

  const clearProjectCollections = useCallback(() => {
    vectorOfLayersRef.current.clear(true);
    rasterLayersCollectionRef.current.clear(true);
    resistanceCollectionRef.current.clear(true);
    corridorCollectionRef.current.clear(true);
    comparisonMapCollectionRef.current.clear(true);
    editableBaselayerCollectionRef.current.clear(true);
    optimalPathCollectionRef.current.clear(true);
    sitesCollectionRef.current.clear(true);
    geoprocessVectorCollectionRef.current.clear(true);
    geoprocessRasterCollectionRef.current.clear(true);
  }, []);

  const addStartPoint = useCallback(feature => {
    startPointSourceRef.current.addFeature(feature);
  }, []);

  const addEndPoint = useCallback(feature => {
    endPointSourceRef.current.addFeature(feature);
  }, []);

  // Layer Management API
  const addVectorLayer = useCallback(layer => {
    if (layer) {
      vectorOfLayersRef.current.push(layer);
    }
  }, []);

  const addRasterLayer = useCallback(layer => {
    if (layer) {
      rasterLayersCollectionRef.current.push(layer);
    }
  }, []);

  const addResistanceLayer = useCallback(layer => {
    if (layer) {
      resistanceCollectionRef.current.push(layer);
    }
  }, []);

  const addCorridorLayer = useCallback(layer => {
    if (layer) {
      corridorCollectionRef.current.push(layer);
    }
  }, []);

  const addOptimalPathLayer = useCallback(layer => {
    if (layer) {
      optimalPathCollectionRef.current.push(layer);
    }
  }, []);

  const addSitesLayer = useCallback(layer => {
    if (layer) {
      sitesCollectionRef.current.push(layer);
    }
  }, []);

  const addEditableBaselayerLayer = useCallback(layer => {
    if (layer) {
      editableBaselayerCollectionRef.current.push(layer);
    }
  }, []);

  const removeEditableBaselayerLayer = useCallback(layer => {
    if (layer) {
      editableBaselayerCollectionRef.current.remove(layer);
    }
  }, []);

  /**
   * Helper function to find a layer by its ID across all managed collections.
   */
  const getLayerById = useCallback((layerId, collectionName = null) => {
    const allCollections = [
      vectorOfLayersRef.current,
      rasterLayersCollectionRef.current,
      resistanceCollectionRef.current,
      corridorCollectionRef.current,
      optimalPathCollectionRef.current,
      geoprocessVectorCollectionRef.current,
      geoprocessRasterCollectionRef.current,
      comparisonMapCollectionRef.current,
      editableBaselayerCollectionRef.current,
      sitesCollectionRef.current
    ];

    if (!collectionName) {
      for (const collection of allCollections) {
        const layers = collection.getArray();
        const foundLayer = layers.find(layer => layer.get("id") === layerId);
        if (foundLayer) {
          return { layer: foundLayer, collection: collection };
        }
      }
    } else {
      const collection = allCollections.find(c => c.name === collectionName);
      const layers = collection.getArray();
      const foundLayer = layers.find(layer => layer.get("id") === layerId);
      if (foundLayer) {
        return { layer: foundLayer, collection: collection };
      }
    }
    return null;
  }, []);

  /**
   * Removes a layer from the map, searching by its ID.
   */
  const removeLayerById = useCallback(
    layerId => {
      const result = getLayerById(layerId);
      if (result) {
        result.collection.remove(result.layer);
        return true;
      }
      return false;
    },
    [getLayerById]
  );

  // Effect to set the map target to the corresondig div id.
  useEffect(() => {
    const olmap = olmapRef.current;

    if (mapTargetElementRef.current && !olmap.getTarget()) {
      olmap.setTarget(mapTargetElementRef.current);
    }
    olmap.getViewport().oncontextmenu = () => false;
  }, []);

  // Post-render events.
  useEffect(() => {
    const olmap = olmapRef.current;
    olmap.once("postrender", () => {
      if (!appState.map2DReady) {
        setMap2DReady(true);
      }
    });
  }, [appState.map2DReady, setMap2DReady]);

  // Render complete events.
  useEffect(() => {
    const olmap = olmapRef.current;
    olmap.once("rendercomplete", () => {
      if (backgrounds.length <= 0) {
        fetchBackgrounds();
      }
    });
  }, [backgrounds.length, fetchBackgrounds]);

  // Effect for updating map on project change
  useEffect(() => {
    if (project.id && projectIdRef.current !== project.id) {
      projectIdRef.current = project.id;
      clearProjectCollections();
      updateMap();

      olmapRef.current.getOverlays().clear(true);
    }
  }, [clearProjectCollections, project.id, updateMap]);

  // Effect to handle mouse move and mouse click in the comments
  // layer to handle hovering and clicking comments on the map.
  useEffect(() => {
    if (!olmapRef.current || !appState.map2DReady) {
      return;
    }

    let hoveredFeature = null;

    // Define a layer filter function to be reused.
    const commentsLayerFilter = layer => {
      return layer && layer.get("pf_comment") === true;
    };

    /**
     * Event listener for the "pointermove" event to handle hover effects.
     */
    function pointerMoveHandler(evt) {
      if (evt.dragging) {
        return;
      }

      // Find the feature rendered on the map at the pixel. This could be a cluster or a single feature.
      // Wrapped in try-catch: a layer disposed in the previous frame can cause OL to throw
      // "Cannot read properties of null (reading 'hasRenderer')" during iteration.
      let hitFeature;
      try {
        hitFeature = olmapRef.current.forEachFeatureAtPixel(
          evt.pixel,
          (feature, layer) => {
            if (layer !== null) {
              return feature;
            }
            return undefined;
          },
          {
            layerFilter: commentsLayerFilter,
            hitTolerance: 4
          }
        );
      } catch {
        return;
      }

      const targetElement = olmapRef.current.getTargetElement();

      // Determine the actual feature to apply hover state to.
      let featureToHover = null;
      if (hitFeature) {
        const clusterFeatures = hitFeature.get("features");
        if (clusterFeatures && clusterFeatures.length === 1) {
          // It's a cluster of one, so we target the inner feature.
          featureToHover = clusterFeatures[0];
        } else if (!clusterFeatures) {
          // It's a single, non-clustered feature.
          featureToHover = hitFeature;
        }
        // If it's a cluster of many, we do nothing to `featureToHover` but still change the cursor.
      }

      // Reset the previously hovered feature if it's different or if we are no longer hovering.
      if (hoveredFeature && hoveredFeature !== featureToHover) {
        hoveredFeature.set("hovered", false);
      }

      hoveredFeature = featureToHover;

      if (hitFeature) {
        // Change cursor for any hit (single, cluster-1, cluster-many).
        targetElement.style.cursor = "pointer";
        if (hoveredFeature && !hoveredFeature.get("selected")) {
          hoveredFeature.set("hovered", true);
        }
      } else {
        targetElement.style.cursor = "";
      }
    }

    /**
     * Event listener for the "click" event to handle selection.
     */
    function clickHandler(evt) {
      let hitFeature;
      try {
        hitFeature = olmapRef.current.forEachFeatureAtPixel(
          evt.pixel,
          (feature, layer) => {
            if (layer !== null) {
              return feature;
            }
            return undefined;
          },
          {
            layerFilter: commentsLayerFilter,
            hitTolerance: 20
          }
        );
      } catch {
        return;
      }

      if (hitFeature) {
        const clusterFeatures = hitFeature.get("features");

        // Case 1: Clicked on a multi-feature cluster -> Zoom to fit them.
        if (clusterFeatures && clusterFeatures.length > 1) {
          // Create a bounding box containing all features in the cluster.
          const extent = createEmpty();
          clusterFeatures.forEach(feature => {
            extend(extent, feature.getGeometry().getExtent());
          });

          // Fit the view to the calculated extent.
          olmapRef.current.getView().fit(extent, {
            duration: 1000,
            padding: [50, 50, 50, 50], // Adds 50px padding around the extent
            maxZoom: 17 // Optional: prevents zooming in too close
          });
        } else {
          // Case 2: Clicked on a single feature or a cluster of one.
          const selectedFeature = clusterFeatures
            ? clusterFeatures[0]
            : hitFeature;

          // Set the feature as selected and not hovered and
          // set the selected thread id with the threads hook
          selectedFeature.set("hovered", false);
          threads.clearAllSelectedThreads(olmapRef.current);
          selectedFeature.set("selected", true);
          threads.setSelectedThreadId(
            olmapRef.current,
            selectedFeature.get("pf_thread_id")
          );
        }
      }
    }

    olmapRef.current.on("pointermove", pointerMoveHandler);
    olmapRef.current.on("click", clickHandler);

    // Cleanup function to remove listeners.
    return () => {
      if (olmapRef.current) {
        olmapRef.current.un("pointermove", pointerMoveHandler);
        olmapRef.current.un("click", clickHandler);
      }
    };
  }, [appState.map2DReady, threads]);

  // Update map when certain context state changes
  useEffect(() => {
    updateMap();
  }, [updateMap]);

  // Effect to update map size when bottom drawer opens/closes or changes height
  useEffect(() => {
    if (olmapRef.current && appState.map2DReady) {
      olmapRef.current.updateSize();
    }
  }, [bottomDrawerOpen, bottomDrawerHeight, appState.map2DReady]);

  // --- Context API Value ---
  const mapApi = useMemo(
    () => ({
      // Data
      olmap: olmapRef.current,
      // Methods
      centerView,
      updateMap,
      addStartPoint,
      addEndPoint,
      // Layer Management Methods
      addVectorLayer,
      addRasterLayer,
      addResistanceLayer,
      addCorridorLayer,
      addOptimalPathLayer,
      addSitesLayer,
      addEditableBaselayerLayer,
      removeEditableBaselayerLayer,
      removeLayerById,
      getLayerById,
      // Layer/Source Collections for direct manipulation if needed
      sources: {
        area: areaSourceRef.current,
        startPoint: startPointSourceRef.current,
        endPoint: endPointSourceRef.current,
        intermediatePoint: intermediatePointSourceRef.current,
        highlightedPoints: highlightedPointsSourceRef.current,
        svPoint: SVPointSourceRef.current
      },
      collections: {
        vectorLayers: vectorOfLayersRef.current,
        rasterLayers: rasterLayersCollectionRef.current,
        editableBaselayers: editableBaselayerCollectionRef.current,
        optimalPaths: optimalPathCollectionRef.current,
        sites: sitesCollectionRef.current
      }
    }),
    [
      centerView,
      updateMap,
      addStartPoint,
      addEndPoint,
      addVectorLayer,
      addRasterLayer,
      addResistanceLayer,
      addCorridorLayer,
      addOptimalPathLayer,
      addSitesLayer,
      addEditableBaselayerLayer,
      removeEditableBaselayerLayer,
      removeLayerById,
      getLayerById
    ]
  );

  return (
    <MapContext.Provider value={mapApi}>
      {/* Map element handling responsive layout */}
      <OpenLayersMap refEle={mapTargetElementRef} />
      {children}
    </MapContext.Provider>
  );
}

Map2dProvider.propTypes = {
  children: PropTypes.node.isRequired,
  threads: PropTypes.shape({
    clearAllSelectedThreads: PropTypes.func.isRequired,
    setSelectedThreadId: PropTypes.func.isRequired
  }).isRequired
};

// Custom hook for consuming the context
const useMap = () => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error("useMap must be used within a MapProvider");
  }
  return context;
};

export { useMap };

export default Map2dProvider;
